import React, { useState } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import confirmDatePlugin from "flatpickr/dist/plugins/confirmDate/confirmDate";
import "flatpickr/dist/plugins/confirmDate/confirmDate.css";
import {
  FiChevronDown,
  FiChevronUp,
  FiToggleLeft,
  FiToggleRight,
  FiMapPin,
  FiCalendar,
  FiPlus,
  FiTrash2,
  FiInfo,
} from "react-icons/fi";

const MAX_DAYS = 3;

// Mirrors publishStartDateOptions in CreateEventForm — same widget, same UX
// across the whole event form.
const dateTimePickerOptions = {
  mode: "single",
  dateFormat: "Y-m-d H:i",
  enableTime: true,
  time_24hr: false,
  altInput: true,
  altFormat: "Y-m-d h:i K",
  plugins: [
    new confirmDatePlugin({
      confirmIcon: "",
      confirmText: "DONE",
      showAlways: true,
      theme: "light",
    }),
  ],
};

const defaultDay = (dayNumber) => ({
  day_number: dayNumber,
  start: "",
  end: "",
  check_in_start: "",
  check_in_end: "",
});

const defaultVenue = () => ({
  name: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  lat: 0,
  lng: 0,
  map_url: "",
});

// Flatpickr gives us a Date in local TZ. We need to serialize back to the
// RFC3339 shape the backend stores (UTC ISO), so the value round-trips
// correctly when reloaded.
const fromDate = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d;
  return new Date(d).toISOString();
};

const parseRfcToDate = (rfc3339) => {
  if (!rfc3339) return null;
  const d = new Date(rfc3339);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Kept around for the unlikely case that any pre-existing draft has a
// datetime-local-style string. Safe to remove later.
// eslint-disable-next-line no-unused-vars
const fromDateTimeLocal = (local) => {
  if (!local) return "";
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
};

const OfflineEventSection = ({ value, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(!!value?.enabled);

  const cfg = {
    enabled: !!value?.enabled,
    venue: { ...defaultVenue(), ...(value?.venue || {}) },
    days: Array.isArray(value?.days) && value.days.length > 0
      ? value.days
      : [defaultDay(1)],
  };

  const update = (patch) => onChange({ ...cfg, ...patch });
  const updateVenue = (patch) => onChange({ ...cfg, venue: { ...cfg.venue, ...patch } });
  const updateDay = (idx, patch) => {
    const next = cfg.days.map((d, i) => (i === idx ? { ...d, ...patch } : d));
    onChange({ ...cfg, days: next });
  };
  const addDay = () => {
    if (cfg.days.length >= MAX_DAYS) return;
    onChange({ ...cfg, days: [...cfg.days, defaultDay(cfg.days.length + 1)] });
  };
  const removeDay = (idx) => {
    if (cfg.days.length <= 1) return;
    const next = cfg.days
      .filter((_, i) => i !== idx)
      .map((d, i) => ({ ...d, day_number: i + 1 }));
    onChange({ ...cfg, days: next });
  };

  const handleToggle = () => {
    const next = !cfg.enabled;
    update({ enabled: next });
    if (next) setIsExpanded(true);
  };

  return (
    <div className="border border-amber-200 rounded-lg bg-amber-50/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded((x) => !x)}
        className="w-full flex items-center justify-between p-4 hover:bg-amber-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <FiMapPin className="text-amber-600" />
          </div>
          <div className="text-left">
            <h4 className="text-base font-semibold text-gray-800">Offline Event Check-In</h4>
            <p className="text-xs text-gray-600">
              {cfg.enabled
                ? `Enabled • ${cfg.days.length} day${cfg.days.length > 1 ? "s" : ""} • QR tickets will be auto-issued`
                : "Off — no tickets generated on participation/sponsorship"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            role="button"
            tabIndex={0}
            aria-label={cfg.enabled ? "Disable offline check-in" : "Enable offline check-in"}
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                handleToggle();
              }
            }}
            className="inline-flex items-center cursor-pointer"
          >
            {cfg.enabled ? (
              <FiToggleRight className="text-green-600" size={32} />
            ) : (
              <FiToggleLeft className="text-gray-400" size={32} />
            )}
          </span>
          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
        </div>
      </button>

      {isExpanded && (
        <div className="p-4 bg-white border-t border-amber-200 space-y-6">
          {!cfg.enabled && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
              <FiInfo className="flex-shrink-0 mt-0.5" />
              <span>
                Flip the toggle above to turn this event into an offline event.
                Participants and sponsors will automatically receive a QR ticket
                by email after participation/payment.
              </span>
            </div>
          )}

          {cfg.enabled && (
            <>
              {/* Venue */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FiMapPin className="text-amber-600" />
                  <h5 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Venue</h5>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Venue Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={cfg.venue.name}
                      onChange={(e) => updateVenue({ name: e.target.value })}
                      placeholder="e.g. Phoenix MarketCity Atrium"
                      className="h-10 border rounded px-3 text-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-1 lg:col-span-2">
                    <label className="text-sm font-medium text-gray-700">Address</label>
                    <input
                      type="text"
                      value={cfg.venue.address}
                      onChange={(e) => updateVenue({ address: e.target.value })}
                      placeholder="Street, area"
                      className="h-10 border rounded px-3 text-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">City</label>
                    <input
                      type="text"
                      value={cfg.venue.city}
                      onChange={(e) => updateVenue({ city: e.target.value })}
                      className="h-10 border rounded px-3 text-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">State</label>
                    <input
                      type="text"
                      value={cfg.venue.state}
                      onChange={(e) => updateVenue({ state: e.target.value })}
                      className="h-10 border rounded px-3 text-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Pincode</label>
                    <input
                      type="text"
                      value={cfg.venue.pincode}
                      onChange={(e) => updateVenue({ pincode: e.target.value })}
                      className="h-10 border rounded px-3 text-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Google Maps URL</label>
                    <input
                      type="url"
                      value={cfg.venue.map_url}
                      onChange={(e) => updateVenue({ map_url: e.target.value })}
                      placeholder="https://maps.google.com/?q=..."
                      className="h-10 border rounded px-3 text-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      value={cfg.venue.lat || ""}
                      onChange={(e) => updateVenue({ lat: parseFloat(e.target.value) || 0 })}
                      className="h-10 border rounded px-3 text-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      value={cfg.venue.lng || ""}
                      onChange={(e) => updateVenue({ lng: parseFloat(e.target.value) || 0 })}
                      className="h-10 border rounded px-3 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Days */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FiCalendar className="text-amber-600" />
                    <h5 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                      Schedule ({cfg.days.length}/{MAX_DAYS} {cfg.days.length === 1 ? "day" : "days"})
                    </h5>
                  </div>
                  {cfg.days.length < MAX_DAYS && (
                    <button
                      type="button"
                      onClick={addDay}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-md font-medium"
                    >
                      <FiPlus /> Add day
                    </button>
                  )}
                </div>

                <div className="flex flex-col gap-4">
                  {cfg.days.map((d, idx) => (
                    <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-500 text-white text-sm font-bold">
                          {idx + 1}
                        </span>
                        <span className="text-sm font-medium text-gray-700">Day {idx + 1}</span>
                        {cfg.days.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeDay(idx)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Remove day"
                          >
                            <FiTrash2 />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-600">Event start</label>
                          <Flatpickr
                            value={parseRfcToDate(d.start)}
                            options={dateTimePickerOptions}
                            onChange={(dates) => updateDay(idx, { start: fromDate(dates?.[0]) })}
                            placeholder="Select date & time"
                            className="h-9 border rounded px-2 text-sm bg-white w-full"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-600">Event end</label>
                          <Flatpickr
                            value={parseRfcToDate(d.end)}
                            options={dateTimePickerOptions}
                            onChange={(dates) => updateDay(idx, { end: fromDate(dates?.[0]) })}
                            placeholder="Select date & time"
                            className="h-9 border rounded px-2 text-sm bg-white w-full"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-600">Check-in opens</label>
                          <Flatpickr
                            value={parseRfcToDate(d.check_in_start)}
                            options={dateTimePickerOptions}
                            onChange={(dates) => updateDay(idx, { check_in_start: fromDate(dates?.[0]) })}
                            placeholder="Select date & time"
                            className="h-9 border rounded px-2 text-sm bg-white w-full"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-600">Check-in closes</label>
                          <Flatpickr
                            value={parseRfcToDate(d.check_in_end)}
                            options={dateTimePickerOptions}
                            onChange={(dates) => updateDay(idx, { check_in_end: fromDate(dates?.[0]) })}
                            placeholder="Select date & time"
                            className="h-9 border rounded px-2 text-sm bg-white w-full"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Same QR works every day. Volunteers scan again on day 2/3 and
                  the server picks the right slot via the check-in window.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineEventSection;
